import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { scrollToForm, trackEvent } from "@/lib/tracking";
import { ArrowRight, ChevronDown, Target, MessageSquare, Code2, ShoppingBag, Menu, X } from "lucide-react";

const WA_NAV = [
  { label: "Home", href: "#top", tid: "home" },
  { label: "Features", href: "#features", tid: "features" },
  { label: "How It Works", href: "#how-it-works", tid: "how-it-works" },
  { label: "Pricing", href: "#pricing", tid: "pricing" },
  { label: "FAQs", href: "#faqs", tid: "faqs" },
];

const SALES_NAV = [
  { label: "How It Works", href: "#how-it-works", tid: "how-it-works" },
  { label: "Solutions", href: "#solutions", tid: "solutions" },
  { label: "Features", href: "#features", tid: "features" },
  { label: "Who It's For", href: "#use-cases", tid: "use-cases" },
  { label: "FAQs", href: "#faqs", tid: "faqs" },
];

const ECOM_NAV = [
  { label: "Solutions", href: "#services", tid: "services" },
  { label: "Online Stores", href: "#online-store", tid: "online-store" },
  { label: "Strategy", href: "#strategy", tid: "strategy" },
  { label: "Marketplaces", href: "#marketplaces", tid: "marketplaces" },
  { label: "How It Works", href: "#how-it-works", tid: "how-it-works" },
  { label: "FAQs", href: "#faqs", tid: "faqs" },
];

const PAGE_NAV = {
  "/ecommerce-solutions": { prefix: "ecom", items: ECOM_NAV, service: "ecommerce_solutions", cta: { label: "Get My eCommerce Plan", href: "#request-quote" } },
  "/whatsapp-automation": { prefix: "wa", items: WA_NAV, service: "whatsapp_automation", cta: { label: "Get a Quote", href: "#request-quote" } },
  "/sales-system": { prefix: "sales", items: SALES_NAV, service: "website_app_creation", cta: { label: "Get a Plan", href: "#request-plan" } },
};

const LOGO =
  "https://customer-assets.emergentagent.com/job_leads-paid-ads/artifacts/c8bafmph_Rekisa%20white.png";

const SERVICES = [
  {
    href: "/paid-ads-management",
    name: "Paid Ads Management",
    desc: "Google, Meta & LinkedIn ads",
    Icon: Target,
    color: "#1a73e8",
  },
  {
    href: "/whatsapp-automation",
    name: "WhatsApp Automation",
    desc: "Chatfuel-powered automation",
    Icon: MessageSquare,
    color: "#25d366",
  },
  {
    href: "/sales-system",
    name: "Rekisa Sales System",
    desc: "Websites + CRM, follow-up & sales pipeline",
    Icon: Code2,
    color: "#0B1120",
  },
  {
    href: "/ecommerce-solutions",
    name: "eCommerce Solutions",
    desc: "Online stores, strategy & marketplaces",
    Icon: ShoppingBag,
    color: "#E8562A",
  },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const salesPage = location.pathname === "/sales-system";
  const waPage = location.pathname === "/whatsapp-automation";
  const ecomPage = location.pathname === "/ecommerce-solutions";
  const pageNav = PAGE_NAV[location.pathname];
  const legalPage = ["/privacy-policy", "/terms"].includes(location.pathname);
  const trackNav = (label, where) => trackEvent("nav_click", { label, location: where, service: pageNav?.service });
  const [mobileOpen, setMobileOpen] = useState(false);
  const onDark = salesPage && !scrolled; // dark navy hero behind a transparent header

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleCta = () => {
    trackEvent("cta_click", { location: "header", path: location.pathname });
    scrollToForm();
  };

  const handleServiceClick = (href) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-zinc-200 shadow-[0_1px_0_rgba(0,0,0,0.02)]"
          : "bg-transparent border-b border-transparent"
      }`}
      style={{ top: "var(--rekisa-announce-h, 0px)" }}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Link
          to="/paid-ads-management"
          className="flex items-center gap-3"
          data-testid="header-logo-link"
          aria-label="Rekisa home"
        >
          <img
            src={LOGO}
            alt="Rekisa"
            width="144" height="36"
            className="h-9 w-auto object-contain"
            style={{ filter: onDark ? "none" : "invert(1)" }}
            loading="eager"
            decoding="async"
          />
        </Link>

        <nav className={`hidden ${pageNav ? "lg:flex" : "md:flex"} items-center gap-8 text-sm ${onDark ? "text-white/75" : "text-zinc-600"}`}>
          <DropdownMenu open={open} onOpenChange={setOpen}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="nav-services-trigger"
                className={`inline-flex items-center gap-1 transition-colors focus:outline-none ${onDark ? "hover:text-white" : "hover:text-zinc-900"}`}
              >
                Services
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`}
                />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              sideOffset={12}
              className="w-[340px] p-2 bg-white border border-zinc-200 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] rounded-2xl"
            >
              {SERVICES.map((s) => {
                const isActive = location.pathname === s.href;
                return (
                  <DropdownMenuItem
                    key={s.href}
                    data-testid={`nav-service-${s.href.replace(/[^a-z]+/gi, "-")}`}
                    onClick={() => handleServiceClick(s.href)}
                    className={`group flex items-start gap-3 rounded-xl p-3 cursor-pointer focus:bg-zinc-50 ${
                      isActive ? "bg-zinc-50" : ""
                    }`}
                  >
                    <div
                      className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: `${s.color}15`, color: s.color }}
                    >
                      <s.Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-[15px] font-bold text-zinc-900 leading-tight">
                        {s.name}
                      </div>
                      <div className="text-xs text-zinc-500 mt-0.5 leading-snug">{s.desc}</div>
                    </div>
                    <ArrowRight
                      className="h-4 w-4 text-zinc-300 group-hover:text-zinc-700 group-hover:translate-x-0.5 transition-all mt-2 shrink-0"
                    />
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          {salesPage ? (
            SALES_NAV.map((l) => (
              <a key={l.tid} href={l.href} onClick={() => trackNav(l.label, "header")} className={`transition-colors ${onDark ? "hover:text-white" : "hover:text-zinc-900"}`} data-testid={`nav-sales-${l.tid}`}>{l.label}</a>
            ))
          ) : waPage ? (
            WA_NAV.map((l) => (
              <a key={l.tid} href={l.href} onClick={() => trackNav(l.label, "header")} className="hover:text-zinc-900 transition-colors" data-testid={`nav-wa-${l.tid}`}>{l.label}</a>
            ))
          ) : ecomPage ? (
            ECOM_NAV.map((l) => (
              <a key={l.tid} href={l.href} onClick={() => trackNav(l.label, "header")} className="hover:text-zinc-900 transition-colors" data-testid={`nav-ecom-${l.tid}`}>{l.label}</a>
            ))
          ) : legalPage ? null : (
            <>
              <a href="#pricing" className="hover:text-zinc-900 transition-colors" data-testid="nav-pricing">
                Pricing
              </a>
              <a href="#faq" className="hover:text-zinc-900 transition-colors" data-testid="nav-faq">
                FAQ
              </a>
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
        {legalPage && (
          <Link to="/sales-system#request-plan" data-testid="header-legal-cta" className="inline-flex items-center rounded-full h-10 px-5 border border-zinc-300 text-sm font-semibold text-zinc-800 hover:border-zinc-500 transition-colors">
            Get a Plan <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        )}
        {!legalPage && (
        <Button
          asChild={salesPage || ecomPage}
          onClick={salesPage || ecomPage ? () => trackEvent("cta_click", { location: "header", path: location.pathname }) : handleCta}
          data-testid="header-cta-request-quote"
          className={
            ecomPage
              ? "bg-[#E8562A] hover:bg-[#cf4720] text-white rounded-full h-10 px-5 font-bold transition-colors"
              : salesPage
              ? "text-[#0B1120] rounded-full h-10 px-5 font-bold transition-all shadow-[0_4px_14px_rgba(190,242,100,0.4)] hover:brightness-105"
              : "bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full h-10 px-5 font-semibold transition-all shadow-[0_4px_14px_rgba(26,115,232,0.35)] hover:shadow-[0_6px_20px_rgba(26,115,232,0.45)]"
          }
          style={salesPage ? { background: "#BEF264" } : undefined}
        >
          {ecomPage ? (
            <a href="#request-quote"><span className="hidden sm:inline">Get My eCommerce Plan</span><span className="sm:hidden">Get a Plan</span><ArrowRight className="ml-1 h-4 w-4" /></a>
          ) : salesPage ? (
            <a href="#request-plan">Get a Plan<ArrowRight className="ml-1 h-4 w-4" /></a>
          ) : (
            <>{waPage ? "Get a Quote" : "Request a Quote"}<ArrowRight className="ml-1 h-4 w-4" /></>
          )}
        </Button>
        )}
        {pageNav && (
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid={`nav-${pageNav.prefix}-mobile-toggle`}
            className="lg:hidden h-10 w-10 rounded-full border border-zinc-200 bg-white flex items-center justify-center text-zinc-800"
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        )}
        </div>
      </div>
      {pageNav && mobileOpen && (
        <nav data-testid={`nav-${pageNav.prefix}-mobile-menu`} className="lg:hidden border-t border-zinc-200 bg-white/95 backdrop-blur-xl px-5 py-3 shadow-lg">
          {pageNav.items.map((l) => (
            <a key={l.tid} href={l.href} onClick={() => { setMobileOpen(false); trackNav(l.label, "header_mobile"); }} data-testid={`nav-${pageNav.prefix}-mobile-${l.tid}`} className="block py-3 text-[15px] font-semibold text-zinc-800 border-b border-zinc-100">{l.label}</a>
          ))}
          <a href={pageNav.cta.href} onClick={() => { setMobileOpen(false); trackNav(pageNav.cta.label, "header_mobile"); }} data-testid={`nav-${pageNav.prefix}-mobile-${pageNav.prefix === "sales" ? "plan" : "quote"}`} className={`mt-3 mb-1 flex items-center justify-center h-11 rounded-full font-semibold ${pageNav.prefix === "sales" ? "bg-[#BEF264] text-[#0B1120]" : pageNav.prefix === "ecom" ? "bg-[#E8562A] text-white" : "bg-[#1a73e8] text-white"}`}>{pageNav.cta.label}</a>
        </nav>
      )}
    </header>
  );
}
